import ClearIcon from '@mui/icons-material/Clear';
import { Box, Chip, SelectChangeEvent } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import useCustomerInitiativeList from './CustomerInitiativeList.hook';
import {
  filtersSectionStyle,
  filtersHeaderStyle,
  filtersTitleStyle,
  filtersResultsStyle,
  filtersResultsValueStyle,
  filtersFormStyle,
  selectedTagsStyle,
  resetButtonStyle, sortSelectStyle, sortSelectWrapperStyle,
} from './CustomerInitiativeList.style';
import { AlertMessageType } from 'basics/enums/maya.enums';
import { SelectOption } from 'basics/options/options.types';
import AlertBanner from 'components/AlertBanner/AlertBanner';
import { FormLayoutContainer } from 'components/FormLayout';
import GridList from 'components/GridList/GridList';
import ClearAllIcon from 'components/Icons/ClearAllIcon';
import MayaLayout from 'components/MayaLayout';
import ConfirmationDialog from 'yoda-ui/components/Dialogs/ConfirmationDialog';
import Select from 'yoda-ui/components/Form/Select';
import MultiSelectYoda from 'yoda-ui/components/Form/SelectYoda/MultiSelectYoda';
import { TagStyle } from 'yoda-ui/components/Form/SelectYoda/MultiSelectYoda/MultiSelectYoda.styles';
import TextYoda from 'yoda-ui/components/Form/TextYoda';
import Loader from 'yoda-ui/components/Loader';
import MenuItem from 'yoda-ui/components/MenuItem';
import { YodaFormProvider } from 'yoda-ui/yodaForm';
import { YodaColors, YodaFontSize } from 'yoda-ui/yodaTheme';

const CustomerInitiativeList: FC = () => {
  const { t } = useTranslation();
  const flags = useFlags();
  const isCiFilterByTherapeuticAreaAndProduct = flags.mayaAkma419EnableSiCiCepListAdditionalFilters;
  const {
    cis,
    ciTitleFieldConfig,
    ciTherapeuticAreaFieldConfig,
    ciProductFieldConfig,
    customerInitiativeListColumnConfig,
    debouncedHandleChangeCiTitle,
    handleChangeCiTherapeuticArea,
    handleChangeCiProduct,
    deleteCiCallback,
    handleClick,
    loading,
    customerInitiativeListTitle,
    newElementButton,
    onPageChange,
    onPageSizeChange,
    open,
    removeCiLoading,
    rowCountState,
    rowId,
    setOpen,
    providerFields,
    selectedFilters,
    handleRemoveChip,
    handleReset,
    hasSelectedFilters,
    ciSortFieldConfig,
    handleSortChange,
    hasActiveFilters,
    ciSortFilter,
  } = useCustomerInitiativeList();

  return (
    <MayaLayout>
      <AlertBanner type={ AlertMessageType.VeevaFatalErrorAlert } />
      <GridList
        getRowId={ (row) => row._id }
        onCellClick={ handleClick }
        loading={ loading }
        title={ customerInitiativeListTitle }
        columnConfig={ customerInitiativeListColumnConfig }
        rows={ cis }
        onPageChange={ onPageChange }
        onPageSizeChange={ onPageSizeChange }
        newElementButton={ newElementButton }
        rowCountState={ rowCountState }
        disableColumnMenu={ true }
        customFilters={
          (
            <YodaFormProvider { ...providerFields }>
              <Box sx={ filtersSectionStyle }>
                <Box sx={ filtersHeaderStyle }>
                  <Box sx={ filtersTitleStyle }>{ t('maya_ci_filter_title') }</Box>
                  {
                    !loading && (
                      <Box sx={ filtersResultsStyle }>
                        { t('maya_ci_filter_results_title') }<Box
                          component="span"
                          sx={ filtersResultsValueStyle }>
                          { t('maya_ci_filter_results_value', { shownItems: cis.length, allItems: rowCountState }) }
                        </Box>
                      </Box>
                    )
                  }
                </Box>

                <FormLayoutContainer sx={ filtersFormStyle }>
                  <TextYoda
                    onChange={ debouncedHandleChangeCiTitle }
                    { ...ciTitleFieldConfig } />
                  {
                    isCiFilterByTherapeuticAreaAndProduct && (
                      <MultiSelectYoda
                        onChange={ handleChangeCiTherapeuticArea }
                        { ...ciTherapeuticAreaFieldConfig } />
                    )
                  }
                  {
                    isCiFilterByTherapeuticAreaAndProduct && (
                      <MultiSelectYoda
                        onChange={ handleChangeCiProduct }
                        { ...ciProductFieldConfig } />
                    )
                  }
                </FormLayoutContainer>

                {
                  hasActiveFilters
                && <Box sx={ selectedTagsStyle }>
                  {
                    selectedFilters.therapeuticArea.map((option) => (
                      <Chip
                        key={ option.id }
                        label={ option.label }
                        sx={ TagStyle }
                        onDelete={ () => handleRemoveChip('therapeuticArea', option.id) }
                        deleteIcon={ <ClearIcon /> }
                      />
                    ))
                  }
                  {
                    selectedFilters.product.map((option) => (
                      <Chip
                        key={ option.id }
                        label={ option.label }
                        sx={ TagStyle }
                        onDelete={ () => handleRemoveChip('product', option.id) }
                        deleteIcon={ <ClearIcon /> }
                      />
                    ))
                  }
                  {
                    hasSelectedFilters && (
                      <Box onClick={ handleReset } sx={ resetButtonStyle }>
                        <ClearAllIcon />
                        { t('actions_reset_label') }
                      </Box>
                    )
                  }
                </Box>
                }
              </Box>
              <Box sx={ sortSelectWrapperStyle }>
                <Select
                  value={ ciSortFilter }
                  onChange={
                    (event: SelectChangeEvent<unknown>, _child: ReactNode) => {
                      const selectedOption = ciSortFieldConfig.options.find((opt) => opt.id === event.target.value);
                      if (selectedOption) {
                        handleSortChange(selectedOption);
                      }
                    }
                  }
                  sx={ sortSelectStyle }
                >
                  {
                    ciSortFieldConfig.options.map(
                      (option: SelectOption) => {
                        return <MenuItem key={ option.id }
                          value={ option.id } sx={ { fontSize: YodaFontSize.small } }>{ option.label }</MenuItem>;
                      },
                    )
                  }
                </Select>
              </Box>
            </YodaFormProvider>
          )
        }
      />
      {
        open && (
          <ConfirmationDialog
            dialogTitle={ t('maya_ci_modal_remove') }
            onConfirm={
              () => {
                deleteCiCallback(rowId);
              }
            }
            onConfirmLabel={ removeCiLoading ? <Loader center size={ 1 } color={ YodaColors.inherit } /> : t('actions_confirm_label') }
            onDismiss={ () => setOpen(false) }
            onDismissLabel={ t('actions_cancel_label') }
          >
          </ConfirmationDialog>
        )
      }
    </MayaLayout>
  );
};

export default CustomerInitiativeList;
